import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Save, Upload, Trash2, Plus, MapPin, Truck, Store, Image as ImageIcon, Loader2, Mail, Share2, User, Lock, UserPlus, Shield } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [admins, setAdmins] = useState([]);
  const logoInputRef = useRef(null);
  
  // Profile state
  const [profileData, setProfileData] = useState({
    name: '',
    email: ''
  });
  
  // Password state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // New admin state
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    password: ''
  });
  
  const [settings, setSettings] = useState({
    logo_url: '',
    store_name: 'ALINA ELEGANCE',
    contact_email: 'alinaelegance1@gmail.com',
    contact_phone: '+1 (613) 123-4567',
    pickup_address: '130 Topham Terrace, Orléans, ON',
    pickup_enabled: true,
    delivery_enabled: true,
    delivery_zones: [
      { zone_id: 'zone_1', name: '0-5 km', min_distance: 0, max_distance: 5, price: 5 },
      { zone_id: 'zone_2', name: '5-15 km', min_distance: 5, max_distance: 15, price: 10 },
      { zone_id: 'zone_3', name: '15-30 km', min_distance: 15, max_distance: 30, price: 15 },
      { zone_id: 'zone_4', name: '30+ km', min_distance: 30, max_distance: 100, price: 25 },
    ],
    social_links: {
      facebook: '',
      instagram: '',
      tiktok: '',
      snapchat: '',
      whatsapp: ''
    }
  });

  useEffect(() => {
    fetchSettings();
    fetchAdmins();
    if (user) {
      setProfileData({ name: user.name || '', email: user.email || '' });
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`);
      if (res.data) {
        setSettings(prev => ({ 
          ...prev, 
          ...res.data,
          social_links: {
            ...prev.social_links,
            ...(res.data.social_links || {})
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(res.data || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const handleUpdateProfile = async () => {
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profil mis à jour !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mot de passe modifié !');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (newAdmin.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setCreatingAdmin(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/admins`, newAdmin, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Administrateur créé !');
      setNewAdmin({ name: '', email: '', password: '' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (userId, adminName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir retirer les droits admin de ${adminName} ?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/admins/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Droits administrateur retirés');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres sauvegardés !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/settings/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update local state with the new logo URL
      const newLogoUrl = res.data.url;
      setSettings(prev => ({ ...prev, logo_url: newLogoUrl }));
      toast.success('Logo mis à jour !');
      
      // Force refresh settings
      fetchSettings();
    } catch (error) {
      toast.error('Erreur lors de l\'upload du logo: ' + (error.response?.data?.detail || error.message));
      console.error(error);
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const addDeliveryZone = () => {
    const lastZone = settings.delivery_zones[settings.delivery_zones.length - 1];
    const newZone = {
      zone_id: `zone_${Date.now()}`,
      name: 'Nouvelle zone',
      min_distance: lastZone ? lastZone.max_distance : 0,
      max_distance: lastZone ? lastZone.max_distance + 10 : 10,
      price: lastZone ? lastZone.price + 5 : 5
    };
    setSettings(prev => ({
      ...prev,
      delivery_zones: [...prev.delivery_zones, newZone]
    }));
  };

  const updateZone = (index, field, value) => {
    const newZones = [...settings.delivery_zones];
    newZones[index] = { ...newZones[index], [field]: value };
    setSettings(prev => ({ ...prev, delivery_zones: newZones }));
  };

  const removeZone = (index) => {
    setSettings(prev => ({
      ...prev,
      delivery_zones: prev.delivery_zones.filter((_, i) => i !== index)
    }));
  };

  const getLogoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads/')) return `${API_URL}${url}`;
    if (url.startsWith('/api/uploads/')) return `${API_URL}${url.replace('/api', '')}`;
    return url;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-settings" className="max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl text-brand-black">Paramètres</h1>
            <p className="text-brand-dark/60 font-body">Configurez votre boutique</p>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-brand-gold text-brand-black hover:bg-brand-gold/90 font-medium"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Sauvegarder
          </Button>
        </div>

        <div className="space-y-6">
          {/* Logo Section */}
          <Card className="border-brand-gold/20 bg-brand-white shadow-sm">
            <CardHeader className="border-b border-brand-gold/10">
              <CardTitle className="flex items-center gap-2 text-brand-black font-heading">
                <ImageIcon className="h-5 w-5 text-brand-gold" /> Logo du Site
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex items-center gap-6">
                <div className="w-32 h-32 border-2 border-dashed border-brand-gold/30 rounded-sm flex items-center justify-center bg-brand-cream overflow-hidden">
                  {settings.logo_url ? (
                    <img 
                      src={getLogoUrl(settings.logo_url)} 
                      alt="Logo" 
                      className="w-full h-full object-contain"
                      onError={(e) => { 
                        console.log('Logo load error:', settings.logo_url);
                        e.target.style.display = 'none'; 
                      }}
                    />
                  ) : (
                    <ImageIcon className="h-12 w-12 text-brand-gold/30" />
                  )}
                </div>
                <div className="space-y-3">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-black"
                  >
                    {uploadingLogo ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {settings.logo_url ? 'Changer le logo' : 'Uploader un logo'}
                  </Button>
                  <p className="text-brand-dark/50 text-xs font-body">PNG, JPG, SVG • Recommandé: 200x200px</p>
                  {settings.logo_url && (
                    <p className="text-green-600 text-xs font-body">✓ Logo actuel : {settings.logo_url.split('/').pop()}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Store Info */}
          <Card className="border-brand-gold/20 bg-brand-white shadow-sm">
            <CardHeader className="border-b border-brand-gold/10">
              <CardTitle className="flex items-center gap-2 text-brand-black font-heading">
                <Store className="h-5 w-5 text-brand-gold" /> Informations Boutique
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              <div>
                <Label className="text-brand-dark font-body">Nom de la boutique</Label>
                <Input
                  value={settings.store_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, store_name: e.target.value }))}
                  className="mt-1 border-brand-gold/30 text-brand-black bg-brand-cream focus:border-brand-gold"
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Email */}
          <Card className="border-brand-gold/20 bg-brand-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-brand-black">
                <Mail className="h-5 w-5 text-brand-gold" /> Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-brand-dark">Email de contact</Label>
                <Input
                  type="email"
                  value={settings.contact_email || 'alinaelegance1@gmail.com'}
                  onChange={(e) => setSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="alinaelegance1@gmail.com"
                  className="mt-1 border-brand-gold/30 text-brand-black"
                />
                <p className="text-brand-dark/50 text-xs mt-1">Les clients pourront vous contacter via le formulaire de contact</p>
              </div>
              <div>
                <Label className="text-brand-dark">Numéro de téléphone</Label>
                <Input
                  type="tel"
                  value={settings.contact_phone || '+1 (613) 123-4567'}
                  onChange={(e) => setSettings(prev => ({ ...prev, contact_phone: e.target.value }))}
                  placeholder="+1 (613) 123-4567"
                  className="mt-1 border-brand-gold/30 text-brand-black"
                />
              </div>
            </CardContent>
          </Card>

          {/* Pickup Section */}
          <Card className="border-brand-gold/20 bg-brand-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-brand-black">
                <MapPin className="h-5 w-5 text-brand-gold" /> Retrait sur Place
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-black font-medium">Activer le retrait</p>
                  <p className="text-brand-dark/60 text-sm">Permettre aux clients de récupérer leurs commandes</p>
                </div>
                <Switch
                  checked={settings.pickup_enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, pickup_enabled: checked }))}
                />
              </div>
              
              {settings.pickup_enabled && (
                <div>
                  <Label className="text-brand-dark">Adresse de retrait</Label>
                  <Input
                    value={settings.pickup_address}
                    onChange={(e) => setSettings(prev => ({ ...prev, pickup_address: e.target.value }))}
                    placeholder="130 Topham Terrace, Orléans, ON"
                    className="mt-1 border-brand-gold/30 text-brand-black"
                  />
                  <p className="text-brand-dark/50 text-xs mt-1">Cette adresse sera affichée aux clients qui choisissent le retrait</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delivery Section */}
          <Card className="border-brand-gold/20 bg-brand-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-brand-black">
                <Truck className="h-5 w-5 text-brand-gold" /> Livraison
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-black font-medium">Activer la livraison</p>
                  <p className="text-brand-dark/60 text-sm">Proposer la livraison à domicile</p>
                </div>
                <Switch
                  checked={settings.delivery_enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, delivery_enabled: checked }))}
                />
              </div>

              {settings.delivery_enabled && (
                <div className="space-y-4 pt-4 border-t border-brand-gold/20">
                  <div className="flex items-center justify-between">
                    <h4 className="text-brand-black font-medium">Zones de livraison (par distance)</h4>
                    <Button variant="outline" size="sm" onClick={addDeliveryZone} className="border-brand-gold text-brand-gold hover:bg-brand-gold/10">
                      <Plus className="h-4 w-4 mr-1" /> Ajouter une zone
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {settings.delivery_zones.map((zone, index) => (
                      <div key={zone.zone_id} className="flex items-center gap-3 p-3 bg-brand-cream rounded-lg border border-brand-gold/20">
                        <div className="flex-1 grid grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs text-brand-dark/60">Nom</Label>
                            <Input
                              value={zone.name}
                              onChange={(e) => updateZone(index, 'name', e.target.value)}
                              className="h-8 text-sm border-brand-gold/30 text-brand-black"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-brand-dark/60">De (km)</Label>
                            <Input
                              type="number"
                              value={zone.min_distance}
                              onChange={(e) => updateZone(index, 'min_distance', parseFloat(e.target.value) || 0)}
                              className="h-8 text-sm border-brand-gold/30 text-brand-black"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-brand-dark/60">À (km)</Label>
                            <Input
                              type="number"
                              value={zone.max_distance}
                              onChange={(e) => updateZone(index, 'max_distance', parseFloat(e.target.value) || 0)}
                              className="h-8 text-sm border-brand-gold/30 text-brand-black"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-brand-dark/60">Prix ($)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={zone.price}
                              onChange={(e) => updateZone(index, 'price', parseFloat(e.target.value) || 0)}
                              className="h-8 text-sm border-brand-gold/30 text-brand-black"
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeZone(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {settings.delivery_zones.length === 0 && (
                    <p className="text-brand-dark/50 text-center py-4">
                      Aucune zone de livraison. Ajoutez-en une pour activer les frais de livraison.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Social Media Links */}
          <Card className="border-brand-gold/20 bg-brand-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-brand-black">
                <Share2 className="h-5 w-5 text-brand-gold" /> Réseaux Sociaux
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-brand-dark/60 text-sm">Ajoutez vos liens de réseaux sociaux pour les afficher dans le pied de page</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-brand-dark flex items-center gap-2">
                    <span className="text-blue-600">📘</span> Facebook
                  </Label>
                  <Input
                    value={settings.social_links?.facebook || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      social_links: { ...prev.social_links, facebook: e.target.value }
                    }))}
                    placeholder="https://facebook.com/votrepage"
                    className="mt-1 border-brand-gold/30 text-brand-black"
                  />
                </div>
                
                <div>
                  <Label className="text-brand-dark flex items-center gap-2">
                    <span className="text-pink-600">📸</span> Instagram
                  </Label>
                  <Input
                    value={settings.social_links?.instagram || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      social_links: { ...prev.social_links, instagram: e.target.value }
                    }))}
                    placeholder="https://instagram.com/votrecompte"
                    className="mt-1 border-brand-gold/30 text-brand-black"
                  />
                </div>
                
                <div>
                  <Label className="text-brand-dark flex items-center gap-2">
                    <span>🎵</span> TikTok
                  </Label>
                  <Input
                    value={settings.social_links?.tiktok || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      social_links: { ...prev.social_links, tiktok: e.target.value }
                    }))}
                    placeholder="https://tiktok.com/@votrecompte"
                    className="mt-1 border-brand-gold/30 text-brand-black"
                  />
                </div>
                
                <div>
                  <Label className="text-brand-dark flex items-center gap-2">
                    <span className="text-yellow-500">👻</span> Snapchat
                  </Label>
                  <Input
                    value={settings.social_links?.snapchat || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      social_links: { ...prev.social_links, snapchat: e.target.value }
                    }))}
                    placeholder="https://snapchat.com/add/votrecompte"
                    className="mt-1 border-brand-gold/30 text-brand-black"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label className="text-brand-dark flex items-center gap-2">
                    <span className="text-green-600">💬</span> WhatsApp (numéro avec code pays)
                  </Label>
                  <Input
                    value={settings.social_links?.whatsapp || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      social_links: { ...prev.social_links, whatsapp: e.target.value }
                    }))}
                    placeholder="https://wa.me/16131234567"
                    className="mt-1 border-brand-gold/30 text-brand-black"
                  />
                  <p className="text-brand-dark/50 text-xs mt-1">Format: https://wa.me/16131234567 (avec indicatif pays sans +)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mon Profil */}
          <Card className="border-brand-gold/20 shadow-lg">
            <CardHeader className="border-b border-brand-gold/10">
              <CardTitle className="flex items-center gap-2 text-brand-black">
                <User className="h-5 w-5 text-brand-gold" />
                Mon Profil
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-brand-dark">Nom</Label>
                  <Input
                    value={profileData.name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Votre nom"
                    className="mt-1 border-brand-gold/30 text-brand-black"
                  />
                </div>
                <div>
                  <Label className="text-brand-dark">Email</Label>
                  <Input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="votre@email.com"
                    className="mt-1 border-brand-gold/30 text-brand-black"
                  />
                </div>
              </div>
              <Button 
                onClick={handleUpdateProfile}
                disabled={savingProfile}
                className="bg-brand-gold text-brand-black hover:bg-brand-gold/90"
              >
                {savingProfile ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Mettre à jour le profil
              </Button>
            </CardContent>
          </Card>

          {/* Changer le mot de passe */}
          <Card className="border-brand-gold/20 shadow-lg">
            <CardHeader className="border-b border-brand-gold/10">
              <CardTitle className="flex items-center gap-2 text-brand-black">
                <Lock className="h-5 w-5 text-brand-gold" />
                Changer le mot de passe
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-brand-dark">Mot de passe actuel</Label>
                  <Input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                    placeholder="••••••••"
                    className="mt-1 border-brand-gold/30 text-brand-black"
                  />
                </div>
                <div>
                  <Label className="text-brand-dark">Nouveau mot de passe</Label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                    placeholder="••••••••"
                    className="mt-1 border-brand-gold/30 text-brand-black"
                  />
                </div>
                <div>
                  <Label className="text-brand-dark">Confirmer le mot de passe</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    placeholder="••••••••"
                    className="mt-1 border-brand-gold/30 text-brand-black"
                  />
                </div>
              </div>
              <Button 
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="bg-brand-gold text-brand-black hover:bg-brand-gold/90"
              >
                {changingPassword ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                Changer le mot de passe
              </Button>
            </CardContent>
          </Card>

          {/* Gestion des Administrateurs */}
          <Card className="border-brand-gold/20 shadow-lg">
            <CardHeader className="border-b border-brand-gold/10">
              <CardTitle className="flex items-center gap-2 text-brand-black">
                <Shield className="h-5 w-5 text-brand-gold" />
                Gestion des Administrateurs
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Liste des admins actuels */}
              <div>
                <h3 className="font-semibold text-brand-black mb-3">Administrateurs actuels</h3>
                <div className="space-y-2">
                  {admins.map((admin) => (
                    <div key={admin.user_id} className="flex items-center justify-between p-3 bg-brand-cream/50 rounded-lg border border-brand-gold/10">
                      <div>
                        <p className="font-medium text-brand-black">{admin.name}</p>
                        <p className="text-sm text-brand-dark/60">{admin.email}</p>
                      </div>
                      {admin.user_id !== user?.user_id && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleRemoveAdmin(admin.user_id, admin.name)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      {admin.user_id === user?.user_id && (
                        <span className="text-xs text-brand-gold bg-brand-gold/10 px-2 py-1 rounded">Vous</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Ajouter un nouvel admin */}
              <div className="border-t border-brand-gold/10 pt-6">
                <h3 className="font-semibold text-brand-black mb-3 flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-brand-gold" />
                  Ajouter un administrateur
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-brand-dark">Nom</Label>
                    <Input
                      value={newAdmin.name}
                      onChange={(e) => setNewAdmin(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Nom de l'admin"
                      className="mt-1 border-brand-gold/30 text-brand-black"
                    />
                  </div>
                  <div>
                    <Label className="text-brand-dark">Email</Label>
                    <Input
                      type="email"
                      value={newAdmin.email}
                      onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="admin@email.com"
                      className="mt-1 border-brand-gold/30 text-brand-black"
                    />
                  </div>
                  <div>
                    <Label className="text-brand-dark">Mot de passe</Label>
                    <Input
                      type="password"
                      value={newAdmin.password}
                      onChange={(e) => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="••••••••"
                      className="mt-1 border-brand-gold/30 text-brand-black"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleCreateAdmin}
                  disabled={creatingAdmin}
                  className="mt-4 bg-brand-gold text-brand-black hover:bg-brand-gold/90"
                >
                  {creatingAdmin ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                  Créer l'administrateur
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
